import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { fetchFirstLoadTasks } from 'reducers/firstLoadTasksReducer';
import { refreshPropertyCounts } from 'reducers/propertyReducer';
import { getPusherConnectionState } from 'data/services/pusherService';
import { isFirstLoadInProgress } from 'utils/firstLoadTasks';

/** How often to re-read tasks and counts while the property's first load is still running. */
export const FIRST_LOAD_POLL_MS = 30000;

/** How often to re-read them while Pusher is not connected and nothing is loading. */
export const PUSHER_DOWN_POLL_MS = 60000;

// The timer ticks at the shortest interval; ticks a longer interval does not need are skipped.
const TICK_MS = FIRST_LOAD_POLL_MS;

/**
 * The poll interval the page needs right now, or null when Pusher alone keeps it current.
 * A hidden tab never polls: every poll is a ~55-query /counts request.
 */
export const getFallbackPollInterval = ({ firstLoadTasks, pusherState, isVisible }) => {
	if (!isVisible) {
		return null;
	}
	if (isFirstLoadInProgress(firstLoadTasks)) {
		return FIRST_LOAD_POLL_MS;
	}
	if (pusherState !== 'connected') {
		return PUSHER_DOWN_POLL_MS;
	}
	return null;
};

/**
 * Safety net for live badge and loader updates: while a first load runs (an event can be
 * missed) or Pusher is not connected, re-read the first-load tasks and badge counts on a
 * timer. Light calls only; the property itself is never reloaded.
 */
const useFallbackPolling = (propertyId) => {
	const dispatch = useDispatch();

	useEffect(() => {
		if (!propertyId) {
			return undefined;
		}

		let msSinceLastPoll = 0;
		const timerId = setInterval(() => {
			msSinceLastPoll += TICK_MS;
			dispatch((innerDispatch, getState) => {
				const { firstLoadTasks } = getState();
				const interval = getFallbackPollInterval({
					firstLoadTasks: firstLoadTasks?.propertyId === propertyId ? firstLoadTasks.data : null,
					pusherState: getPusherConnectionState(),
					isVisible: document.visibilityState !== 'hidden',
				});
				if (interval === null || msSinceLastPoll < interval) {
					return;
				}
				msSinceLastPoll = 0;
				innerDispatch(fetchFirstLoadTasks(propertyId));
				innerDispatch(refreshPropertyCounts(propertyId));
			});
		}, TICK_MS);

		return () => clearInterval(timerId);
	}, [propertyId, dispatch]);
};

export default useFallbackPolling;
